import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { officeSuppliesApi, FB510, CreateRequest, UpdateRequest } from '../../../services/officeSuppliesApi';

export type Mode = 'browse' | 'add' | 'edit';

interface FormFields {
  fb510_01: string;
  fb510_05: string;
  fb510_06: string;
  fb510_11: string;
  fb510_12: string;
  fb510_13: string;
  fb510_15: number;
  fb510_20: number;
  fb510_25: string;
  fb510_26: string;
}

const emptyFields: FormFields = {
  fb510_01: '', fb510_05: '', fb510_06: '',
  fb510_11: '', fb510_12: '', fb510_13: '',
  fb510_15: 0, fb510_20: 0, fb510_25: '', fb510_26: '',
};

export function useOfficeSuppliesForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FB510[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState<FormFields>(emptyFields);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await officeSuppliesApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = (rec: FB510) => {
    setFields({
      fb510_01: rec.fb510_01, fb510_05: rec.fb510_05, fb510_06: rec.fb510_06,
      fb510_11: rec.fb510_11, fb510_12: rec.fb510_12, fb510_13: rec.fb510_13,
      fb510_15: rec.fb510_15, fb510_20: rec.fb510_20,
      fb510_25: rec.fb510_25, fb510_26: rec.fb510_26,
    });
  };

  useEffect(() => {
    if (current && mode === 'browse') syncFields(current);
  }, [current, mode]);

  useEffect(() => {
    loadRecords().then(data => { if (data.length > 0) setCurrentIdx(0); });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setFields(emptyFields); };
  const startEdit = () => { if (!current) return; setMode('edit'); syncFields(current); };
  const cancel = () => { setMode('browse'); if (current) syncFields(current); };

  const setField = (key: keyof FormFields, val: string | number) => {
    setFields(prev => ({ ...prev, [key]: val }));
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        const req: CreateRequest = { ...fields };
        await officeSuppliesApi.create(req);
        message.success('新增成功');
      } else {
        const req: UpdateRequest = {
          fb510_05: fields.fb510_05, fb510_06: fields.fb510_06,
          fb510_11: fields.fb510_11, fb510_12: fields.fb510_12, fb510_13: fields.fb510_13,
          fb510_15: fields.fb510_15, fb510_20: fields.fb510_20,
          fb510_25: fields.fb510_25, fb510_26: fields.fb510_26,
        };
        await officeSuppliesApi.update(current!.fb510_01, req);
        message.success('修改成功');
      }
      const data = await loadRecords();
      const code = mode === 'add' ? fields.fb510_01 : current!.fb510_01;
      const idx = data.findIndex(r => r.fb510_01 === code);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await officeSuppliesApi.delete(current.fb510_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.fb510_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    fields, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  };
}
